import { filterOnlyHavingParameterSet } from '../filters/byParameterName.js';
import StringifiedParameterFormatter from '../formatters/StringifiedParameterFormatter.js';
import EnumValueProvider from './EnumValueProvider.js';
import buildFiltersForHittingChart from '../filters/hittingChartsFilters.js';
import { HitData, ResultData, PitchData, HitReprocessed } from '../enums/index.js';
import UniqueParametersForPitchAndHitProvider from './UniqueParametersForPitchAndHitProvider.js';
import { Series, ChartSeries, BaseballResultsProcessor } from '../entities/index.js';

const buildFiltersArray = function({
    // tagging context
    batterId, pitchResults, pitchTypes, zones, pitcherIds, pitcherHandedness,
    // scoring context
    outs, balls, strikes, runnersOn,
}) {
    return buildFiltersForHittingChart({
        batterId, pitchResults, pitchTypes, zones, pitcherIds, pitcherHandedness,
        outs, balls, strikes, runnersOn
    }, true);
};

const getSeriesForHitTracking = function(
    results, customResultsProcessor, hitDataKey,
    {
        // tagging context
        batterId, pitchResults, pitchTypes, zones, pitcherIds, pitcherHandedness,
        // scoring context
        outs, balls, strikes, runnersOn,
    } = {}
) {
    if (!hitDataKey) {
        throw new TypeError('Name of hit data parameter is required');
    }

    if (typeof hitDataKey !== 'string') {
        throw new TypeError('Name of hit data parameter must be an string');
    }

    let hitParameter = EnumValueProvider.getValue(hitDataKey, HitData);

    if (!hitParameter) {
        throw new Error(`Unable to determine definition for parameter ${hitDataKey}`);
    }

    let parameterKey = hitParameter.key;
    let extractProperty = hitParameter.property;
    let paramParent = '';

    if (hitParameter.parent) {
        paramParent = EnumValueProvider.getValue(hitParameter.parent, HitData);

        if (!paramParent) {
            throw new Error(`Unable to determine definition for parent parameter ${hitParameter.parent}`);
        }

        parameterKey = paramParent.key;
    }

    let trackingChartSeries = [];
    let resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();
    let pitchesAndHits = BaseballResultsProcessor.matchPitchesAndHits(results);

    let trackingChartFilters = buildFiltersArray({
        batterId, pitchResults, pitchTypes, zones, pitcherIds, pitcherHandedness,
        outs, balls, strikes, runnersOn
    });

    trackingChartFilters = trackingChartFilters.concat(
        filterOnlyHavingParameterSet(['hit'].concat(ResultData.getDataPath(parameterKey)))
    );

    let hitsWithParameter =  resultsProcessor.filterData(pitchesAndHits, trackingChartFilters);
    let resultsMapped = resultsProcessor.mapCollectionProperties(hitsWithParameter, [
        ['hit', ResultData.Id.key],
        ['hit'].concat(ResultData.getDataPath(parameterKey)),
        ['hit'].concat(ResultData.getDataPath(HitReprocessed.PlateAppearanceR.key)),
        ['pitch'].concat(ResultData.getDataPath(PitchData.BeforePlayPlateAppearance.key)),
    ]);

    // TODO - move to separate method
    let dataForParameter = new Series(hitDataKey, 2);

    for (let i = 0; i < resultsMapped.length; i++) {
        let paramValue = null;
        let category = null;

        // get value for Y
        if (paramParent && paramParent.stringifiedAs) {
            let formatted = StringifiedParameterFormatter(
                resultsMapped[i][parameterKey], ';', paramParent.stringifiedAs, parseFloat
            );

            if (formatted && formatted.hasOwnProperty(extractProperty)) {
                paramValue = formatted[extractProperty];
            }
            else { 
                continue;
            }
        }
        else {
            paramValue = resultsMapped[i][parameterKey];
        }

        // get value for X
        // prefer scoring data over re-calculated params
        if (
            resultsMapped[i].hasOwnProperty(PitchData.BeforePlayPlateAppearance.key)
            && resultsMapped[i][PitchData.BeforePlayPlateAppearance.key] > 0
        ) {
            category = resultsMapped[i][PitchData.BeforePlayPlateAppearance.key];
        }
        else {
            category = resultsMapped[i][HitReprocessed.PlateAppearanceR.key];
        }

        dataForParameter.add(resultsMapped[i][ResultData.Id.key], category, paramValue);
    }

    trackingChartSeries.push(dataForParameter);

    return new ChartSeries(trackingChartSeries, dataForParameter.getDimension(1));
};

const listUniqueParametersForHitTrackingChart = function(
    results, hitDataKey, customResultsProcessor,
    {
        // tagging context
        batterId, pitchResults, pitchTypes, zones, pitcherIds, pitcherHandedness,
        // scoring context
        outs, balls, strikes, runnersOn,
    } = {},
    pitchParameters, hitParameters, pitchToHitParamsToMerge, combinedPitchResult
) {
    pitchParameters = pitchParameters || [
        // tagging context
        [ResultData.BatterTagId.key],
        [ResultData.TagId.key],
        ResultData.getDataPath(PitchData.SwingHandedness.key),
        ResultData.getDataPath(PitchData.Handedness.key),
        ResultData.getDataPath(PitchData.Result.key),
        ResultData.getDataPath(PitchData.Classification.key),
        ResultData.getDataPath(PitchData.Mechanics.key),
        // scoring context
        ResultData.getDataPath(PitchData.BeforePlayOuts.key),
        ResultData.getDataPath(PitchData.BeforePlayBalls.key),
        ResultData.getDataPath(PitchData.BeforePlayStrikes.key),
        ResultData.getDataPath(PitchData.BeforePlayRunnersOn.key),
    ];

    hitParameters = hitParameters || [
        [ResultData.TagId.key],
        ResultData.getDataPath(HitData.SwingHandedness.key),
    ];

    pitchToHitParamsToMerge = pitchToHitParamsToMerge || [
        0, undefined, 1
    ];

    if (!hitDataKey) {
        throw new TypeError('Name of hit data parameter is required');
    }

    if (typeof hitDataKey !== 'string') {
        throw new TypeError('Name of hit data parameter must be an string');
    }

    let hitParameter = EnumValueProvider.getValue(hitDataKey, HitData);

    if (!hitParameter) {
        throw new Error(`Unable to determine definition for parameter ${hitDataKey}`);
    }

    let parameterKey = hitParameter.key;

    if (hitParameter.parent) {
        const paramParent = EnumValueProvider.getValue(hitParameter.parent, HitData);

        if (!paramParent) {
            throw new Error(`Unable to determine definition for parent parameter ${hitParameter.parent}`);
        }

        parameterKey = paramParent.key;
    }

    const resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();
    let pitchesAndHits = BaseballResultsProcessor.matchPitchesAndHits(results);

    let trackingChartFilters = buildFiltersArray({
        batterId, pitchResults, pitchTypes, zones, pitcherIds, pitcherHandedness,
        outs, balls, strikes, runnersOn
    });

    trackingChartFilters = trackingChartFilters.concat(filterOnlyHavingParameterSet(['hit'].concat(ResultData.getDataPath(parameterKey))));

    let hitsWithParameter =  resultsProcessor.filterData(pitchesAndHits, trackingChartFilters);

    return UniqueParametersForPitchAndHitProvider(
        pitchParameters.map(x => ['pitch'].concat(x)),
        hitParameters.map(x => ['hit'].concat(x)),
        pitchToHitParamsToMerge,
        hitsWithParameter, // it is ok, hit and pitch filtering is included on parameters level
        hitsWithParameter,
        resultsProcessor,
        combinedPitchResult
    );
};

export default getSeriesForHitTracking;

export {
    getSeriesForHitTracking, listUniqueParametersForHitTrackingChart
};